import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react'
import { useLayoutContext } from '@/context/layout.context'
import { useAuth } from '@/hooks/use-auth'
import { APP_NAME } from '@/constants'
import { NAV_ITEMS, type NavItem } from '@/constants/navigation'
import { cn } from '@/utils'

export function Sidebar() {
  const { isCollapsed, isMobileOpen, toggleCollapsed, closeMobile } = useLayoutContext()
  const { user } = useAuth()

  // 'profile' siempre visible (no tiene gate propio, igual que antes); el
  // resto se filtra por los permisos de menú del rol del usuario logueado
  // — persistidos desde el Gestor de roles, no por el array `roles`
  // hardcodeado de NAV_ITEMS (que ahora solo sirve como fuente de datos
  // para armar esos permisos, ver RolesView.tsx).
  const allowedKeys = new Set(['profile', ...(user?.menuSections ?? [])])
  const visibleItems = NAV_ITEMS.filter((item) => allowedKeys.has(item.key)).map((item) =>
    item.children
      ? { ...item, children: item.children.filter((child) => allowedKeys.has(child.key)) }
      : item
  )

  return (
    <aside
      className={cn(
        'flex flex-col border-r border-border bg-sidebar transition-all duration-200 ease-in-out',
        // Mobile: fixed drawer, always full width
        'fixed inset-y-0 left-0 z-50 w-64',
        isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        // Desktop: part of flex layout, respects collapsed state
        'lg:relative lg:inset-auto lg:z-auto lg:translate-x-0',
        isCollapsed ? 'lg:w-16' : 'lg:w-64'
      )}
    >
      {/* Brand header */}
      <div
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 border-b border-border px-3',
          isCollapsed ? 'h-14 lg:h-auto lg:flex-col lg:justify-center lg:gap-1.5 lg:py-2' : 'h-14'
        )}
      >
        <div
          className={cn('flex min-w-0 items-center gap-3', isCollapsed ? 'lg:contents' : 'flex-1')}
        >
          <a
            href="/dashboard"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-sidebar"
            aria-label="Ir al dashboard"
          >
            <span className="text-xs font-bold text-primary-foreground" aria-hidden="true">
              C
            </span>
          </a>

          <span
            className={cn(
              'truncate text-sm font-semibold text-foreground',
              isCollapsed && 'lg:hidden'
            )}
          >
            {APP_NAME}
          </span>
        </div>

        {/* Desktop collapse toggle */}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={isCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
          aria-expanded={!isCollapsed}
          className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring lg:flex"
        >
          {isCollapsed ? (
            <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
          ) : (
            <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
          )}
        </button>

        {/* Mobile close */}
        <button
          type="button"
          onClick={closeMobile}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring lg:hidden"
          aria-label="Cerrar menú de navegación"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2" aria-label="Menú principal">
        <ul className="space-y-0.5">
          {visibleItems.map((item) =>
            item.children && item.children.length > 0 ? (
              <SidebarNavGroup
                key={item.key}
                item={item}
                isCollapsed={isCollapsed}
                onNavigate={closeMobile}
              />
            ) : (
              <SidebarNavItem
                key={item.key}
                item={item}
                isCollapsed={isCollapsed}
                onNavigate={closeMobile}
              />
            )
          )}
        </ul>
      </nav>
    </aside>
  )
}

interface SidebarNavItemProps {
  item: NavItem
  isCollapsed: boolean
  onNavigate: () => void
}

function SidebarNavItem({ item, isCollapsed, onNavigate }: SidebarNavItemProps) {
  return (
    <li>
      <NavLink
        to={item.path}
        onClick={onNavigate}
        title={isCollapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-ring',
            isActive
              ? 'bg-primary/10 font-medium text-primary'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            isCollapsed && 'lg:justify-center lg:px-2'
          )
        }
      >
        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className={cn('truncate', isCollapsed && 'lg:hidden')}>{item.label}</span>
      </NavLink>
    </li>
  )
}

function SidebarNavGroup({ item, isCollapsed, onNavigate }: SidebarNavItemProps) {
  const { pathname } = useLocation()
  const isChildActive = item.children?.some((child) => pathname.startsWith(child.path)) ?? false
  const [open, setOpen] = useState(isChildActive)

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        title={isCollapsed ? item.label : undefined}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          isChildActive
            ? 'font-medium text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          isCollapsed && 'lg:justify-center lg:px-2'
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className={cn('flex-1 truncate text-left', isCollapsed && 'lg:hidden')}>
          {item.label}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            open && 'rotate-180',
            isCollapsed && 'lg:hidden'
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul
          className={cn(
            'mt-0.5 space-y-0.5 border-l border-border pl-3',
            isCollapsed && 'lg:hidden'
          )}
        >
          {item.children?.map((child) => (
            <SidebarNavItem
              key={child.key}
              item={child}
              isCollapsed={false}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  )
}
